import React from 'react';
import { DSIcon, DSTypeaheadFilterSelect, searchFieldProps } from '../primitives';

/**
 * The one way to pick a host.
 *
 * Picking a host has been rebuilt at every call site: a plain `<select>` here (unusable
 * against 400 members), a hand-rolled search input with a results list there, and three
 * copies of the same `DSTypeaheadFilterSelect` wiring elsewhere, each with its own idea
 * of how to render a person's name. This is that pattern, once.
 *
 * It is a pattern rather than a primitive because it knows what a host is. The primitives
 * stay generic; this owns the domain rules - how a host is labelled, what the copy says,
 * and that a host list is always searchable because it is always long.
 *
 * Single or multiple:
 *
 *   <HostPicker label="Host" hosts={users} value={hostId} onChange={setHostId} />
 *   <HostPicker label="Default Hosts" multiple hosts={users} value={ids} onChange={setIds} />
 *
 * In single mode `value` is an id (or '') and `onChange` is called with an id or ''.
 * In multiple mode both are arrays.
 *
 * `inline` renders the search and the list directly rather than behind a trigger. Use it
 * inside a sheet or a panel: the default is a popover, and a popover opened inside a
 * content-height sheet is clipped by it - which is exactly what happened on the event
 * manage screen, where assigning a host showed one and a half names and no way to scroll.
 */

export const hostDisplayName = (host) => {
  if (!host) {
    return '';
  }

  const name = [host.first_name, host.last_name].filter(Boolean).join(' ').trim();

  return name || host.name || host.email || `User ${host.id}`;
};

export const toHostOptions = (hosts = [], excludeIds = []) => {
  const excluded = new Set(excludeIds.map(Number));

  return hosts
    .filter((host) => !excluded.has(Number(host.id)))
    // The hint tells two people with the same name apart, which happens often enough in a
    // club of 400 that a bare name is not a safe thing to pick from.
    .map((host) => ({
      value: host.id,
      label: hostDisplayName(host),
      hint: host.email || host.username || ''
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Search plus a plain list of people, for use where a popover would be clipped. */
function InlineHostList({ id, options, selectedValues, multiple, emptyMessage, onPick }) {
  const [query, setQuery] = React.useState('');

  const term = query.trim().toLowerCase();
  const matches = term
    ? options.filter((option) =>
      option.label.toLowerCase().includes(term) || option.hint.toLowerCase().includes(term))
    : options;

  return (
    <div className="ds-host-picker__inline">
      <div className="ds-host-picker__search">
        <DSIcon name="search" size="sm" tone="muted" />
        <input
          {...searchFieldProps}
          id={id}
          className="ds-host-picker__input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name or email"
        />
      </div>

      <div className="ds-host-picker__results" role="listbox" aria-multiselectable={multiple || undefined}>
        {matches.length === 0 ? (
          <p className="ds-host-picker__empty">{emptyMessage}</p>
        ) : matches.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={`ds-host-picker__row${isSelected ? ' is-selected' : ''}`}
              onClick={() => onPick(option.value)}
            >
              <span className="ds-host-picker__who">
                <span className="ds-host-picker__name">{option.label}</span>
                {option.hint && <span className="ds-host-picker__hint">{option.hint}</span>}
              </span>
              {isSelected && <DSIcon name="check" size="sm" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HostPicker({
  id,
  label = 'Host',
  helpText,
  hosts = [],
  value,
  onChange,
  multiple = false,
  excludeIds = [],
  placeholder,
  emptyMessage = 'No hosts found',
  disabled = false,
  inline = false
}) {
  const options = React.useMemo(() => toHostOptions(hosts, excludeIds), [hosts, excludeIds]);

  const selectedValues = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }

    return value === undefined || value === null || value === '' ? [] : [value];
  }, [multiple, value]);

  const handleChange = (nextValues) => {
    if (!onChange) {
      return;
    }

    if (multiple) {
      onChange(nextValues);
      return;
    }

    // Single mode: the typeahead is inherently multi-select, so the most recent pick wins
    // and picking the selected host again clears it.
    onChange(nextValues.length > 0 ? nextValues[nextValues.length - 1] : '');
  };

  const resolvedPlaceholder = placeholder
    || (multiple ? 'Search for hosts...' : 'Search for a host...');

  if (inline) {
    return (
      <div className="ds-host-picker" data-disabled={disabled ? 'true' : undefined}>
        {label ? <span className="ds-label">{label}</span> : null}
        <InlineHostList
          id={id}
          options={options}
          selectedValues={selectedValues}
          multiple={multiple}
          emptyMessage={emptyMessage}
          onPick={(value) => {
            if (!onChange) return;

            if (multiple) {
              onChange(selectedValues.includes(value)
                ? selectedValues.filter((item) => item !== value)
                : [...selectedValues, value]);
              return;
            }

            // Single mode: picking the selected host again clears it, matching the
            // popover's behaviour so the two modes do not disagree.
            onChange(selectedValues.includes(value) ? '' : value);
          }}
        />
        {helpText ? <p className="ds-host-picker__help">{helpText}</p> : null}
      </div>
    );
  }

  return (
    <div className="ds-host-picker" data-disabled={disabled ? 'true' : undefined}>
      <DSTypeaheadFilterSelect
        id={id}
        label={label}
        options={options}
        selectedValues={selectedValues}
        placeholder={resolvedPlaceholder}
        emptyMessage={emptyMessage}
        onChange={handleChange}
      />
      {helpText ? <p className="ds-host-picker__help">{helpText}</p> : null}
    </div>
  );
}

export default HostPicker;
